import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { VideoBackground } from '../background/VideoBackground';
import { Timer, Music, Users, Trophy, Shield, Lock } from 'lucide-react';

export function LoginScreen() {
  const [loading, setLoading] = useState<'discord' | 'google' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleDiscordLogin = async () => {
    setLoading('discord');
    setError(null);

    try {
      const redirectUrl = `${window.location.protocol}//${window.location.host}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: redirectUrl,
          scopes: 'identify guilds',
        },
      });

      if (error) {
        console.error('OAuth error:', error);
        setError('Failed to connect to Discord. Please try again.');
        setLoading(null);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
      setLoading(null);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading('google');
    setError(null);

    try {
      const redirectUrl = `${window.location.protocol}//${window.location.host}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        console.error('Google OAuth error:', error);
        setError('Failed to connect to Google. Please try again.');
        setLoading(null);
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError('An unexpected error occurred. Please try again.');
      setLoading(null);
    }
  };

  // Privacy Policy Modal
  if (showPrivacy) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        <VideoBackground />
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
          <div className="max-w-2xl w-full bg-black/60 backdrop-blur-md rounded-2xl p-8 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-white text-3xl font-bold">Privacy Policy</h1>
              <button
                onClick={() => setShowPrivacy(false)}
                className="text-white/60 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            <div className="text-white/80 space-y-4 text-sm">
              <p>
                <strong>Last updated:</strong> December 25, 2024
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">1. Information We Collect</h2>
              <p>When you use Study Saga, we collect:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Account Information:</strong> Your username, email, and profile picture
                  from Discord or Google when you sign in.
                </li>
                <li>
                  <strong>Usage Data:</strong> Your study session history, XP earned, level
                  progress, and timer settings.
                </li>
                <li>
                  <strong>Device Information:</strong> Browser type and general device information
                  for app optimization.
                </li>
              </ul>

              <h2 className="text-white text-xl font-semibold mt-6">
                2. How We Use Your Information
              </h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and maintain the Study Saga service</li>
                <li>Track your study progress and achievements</li>
                <li>Sync your data across devices</li>
                <li>Improve our services</li>
              </ul>

              <h2 className="text-white text-xl font-semibold mt-6">3. Data Sharing</h2>
              <p>We do not sell your personal data. We may share data only:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  With service providers who help us operate the app (Supabase for database,
                  Discord/Google for authentication)
                </li>
                <li>If required by law</li>
              </ul>

              <h2 className="text-white text-xl font-semibold mt-6">4. Data Security</h2>
              <p>
                We use industry-standard security measures including encrypted connections (HTTPS),
                secure authentication, and protected databases.
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">5. Your Rights</h2>
              <p>You can:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access your data through your profile</li>
                <li>Request deletion of your account</li>
                <li>Contact us with privacy concerns</li>
              </ul>

              <h2 className="text-white text-xl font-semibold mt-6">6. Contact</h2>
              <p>
                For privacy questions, contact us at:{' '}
                <a href="mailto:studysagaapp@gmail.com" className="text-blue-400 hover:underline">
                  studysagaapp@gmail.com
                </a>
              </p>
            </div>
            <button
              onClick={() => setShowPrivacy(false)}
              className="mt-6 w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Terms of Service Modal
  if (showTerms) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        <VideoBackground />
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
          <div className="max-w-2xl w-full bg-black/60 backdrop-blur-md rounded-2xl p-8 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-white text-3xl font-bold">Terms of Service</h1>
              <button
                onClick={() => setShowTerms(false)}
                className="text-white/60 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            <div className="text-white/80 space-y-4 text-sm">
              <p>
                <strong>Last updated:</strong> December 25, 2024
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">1. Acceptance of Terms</h2>
              <p>
                By using Study Saga, you agree to these terms. If you don't agree, please don't use
                the service.
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">2. Description of Service</h2>
              <p>
                Study Saga is a Pomodoro timer and productivity application that helps users track
                their study sessions, earn XP, and level up their productivity.
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">3. User Accounts</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>You must provide accurate information when creating an account</li>
                <li>You are responsible for maintaining the security of your account</li>
                <li>You must not share your account with others</li>
              </ul>

              <h2 className="text-white text-xl font-semibold mt-6">4. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Abuse, harass, or harm other users</li>
                <li>Attempt to cheat or manipulate the XP/leveling system</li>
                <li>Use the service for any illegal purposes</li>
                <li>Attempt to access other users' accounts</li>
              </ul>

              <h2 className="text-white text-xl font-semibold mt-6">5. Content</h2>
              <p>
                You retain ownership of any content you create. By using our chat features, you
                grant us permission to display your messages to other users.
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">6. Termination</h2>
              <p>
                We may suspend or terminate your account if you violate these terms. You may delete
                your account at any time.
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">7. Disclaimer</h2>
              <p>
                Study Saga is provided "as is" without warranties. We are not responsible for any
                loss of data or interruption of service.
              </p>

              <h2 className="text-white text-xl font-semibold mt-6">8. Contact</h2>
              <p>
                For questions about these terms, contact us at:{' '}
                <a href="mailto:studysagaapp@gmail.com" className="text-blue-400 hover:underline">
                  studysagaapp@gmail.com
                </a>
              </p>
            </div>
            <button
              onClick={() => setShowTerms(false)}
              className="mt-6 w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Landing Page
  return (
    <div className="relative min-h-screen overflow-hidden">
      <VideoBackground />

      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="relative z-10 py-4 px-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🍅</span>
              <span className="text-white font-bold text-xl">Study Saga</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-white/60 hover:text-white transition-colors"
              >
                Privacy
              </button>
              <button
                onClick={() => setShowTerms(true)}
                className="text-white/60 hover:text-white transition-colors"
              >
                Terms
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Hero */}
            <div className="mb-12">
              <h1 className="text-white text-5xl md:text-6xl font-bold mb-4">Study Saga</h1>
              <p className="text-white/70 text-xl md:text-2xl mb-2">Level up your productivity</p>
              <p className="text-white/50 text-lg max-w-2xl mx-auto">
                Pomodoro timer meets gamification. Track your study sessions, earn XP, level up, and
                compete with 6,500+ students worldwide.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Timer className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm">Pomodoro Timer</h3>
                <p className="text-white/50 text-xs mt-1">Customizable focus sessions</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm">XP & Leveling</h3>
                <p className="text-white/50 text-xs mt-1">Earn XP, level up to 100</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Music className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm">Lofi Music</h3>
                <p className="text-white/50 text-xs mt-1">Built-in ambient sounds</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm">Community</h3>
                <p className="text-white/50 text-xs mt-1">Study with 6,500+ users</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm">Discord Activity</h3>
                <p className="text-white/50 text-xs mt-1">Study in voice channels</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Lock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold text-sm">Synced Progress</h3>
                <p className="text-white/50 text-xs mt-1">Web & Discord linked</p>
              </div>
            </div>

            {/* Login Card */}
            <div className="max-w-md mx-auto bg-black/40 backdrop-blur-md rounded-2xl p-8 border border-white/10">
              <h2 className="text-white text-2xl font-bold mb-2">Get Started</h2>
              <p className="text-white/60 text-sm mb-6">Sign in to start your focus journey</p>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Discord Login */}
                <button
                  onClick={handleDiscordLogin}
                  disabled={loading !== null}
                  className="w-full px-6 py-3 bg-[#5865F2] text-white rounded-lg font-bold hover:bg-[#4752C4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {loading === 'discord' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 71 55"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"
                          fill="currentColor"
                        />
                      </svg>
                      Sign in with Discord
                    </>
                  )}
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-white/20" />
                  <span className="text-white/40 text-sm">or</span>
                  <div className="flex-1 h-px bg-white/20" />
                </div>

                {/* Google Login */}
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading !== null}
                  className="w-full px-6 py-3 bg-white text-gray-800 rounded-lg font-bold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {loading === 'google' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.57C14.73 18.23 13.48 18.63 12 18.63C9.13 18.63 6.72 16.69 5.84 14.09H2.18V16.94C3.99 20.53 7.7 23 12 23Z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09C5.62 13.43 5.49 12.73 5.49 12C5.49 11.27 5.62 10.57 5.84 9.91V7.06H2.18C1.43 8.55 1 10.22 1 12C1 13.78 1.43 15.45 2.18 16.94L5.84 14.09Z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38C13.62 5.38 15.06 5.94 16.21 7.02L19.36 3.87C17.45 2.09 14.97 1 12 1C7.7 1 3.99 3.47 2.18 7.06L5.84 9.91C6.72 7.31 9.13 5.38 12 5.38Z"
                          fill="#EA4335"
                        />
                      </svg>
                      Sign in with Google
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="relative z-10 py-6 px-6 bg-white">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-gray-600">© 2024 Study Saga. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a
                href="/privacy"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacy(true);
                }}
                className="text-gray-800 hover:text-black font-medium underline"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTerms(true);
                }}
                className="text-gray-800 hover:text-black font-medium underline"
              >
                Terms of Service
              </a>
              <a
                href="mailto:studysagaapp@gmail.com"
                className="text-gray-800 hover:text-black font-medium"
              >
                Contact
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
